import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import Icon from '@jetbrains/ring-ui/components/icon';
import classNames from 'classnames';
import Button from '@jetbrains/ring-ui/components/button/button';
import trash from '@jetbrains/icons/trash';
import { H3 } from '@jetbrains/ring-ui/components/heading/heading';
import chevronDown from '@jetbrains/icons/chevron-down';
import info from '@jetbrains/icons/info';
import { Badge } from '@common/badge/general-badge/badge';
import { assignTestId } from '@common/infra/tests/assign-test-id';
import { Collapsible } from '@common/collapsible';
import { usePrevious } from '@common/hooks/use-previous';
import { useIsFocused } from '@common/hooks/use-is-focused';
import { useFeatureFlag } from '@common/server-config-context/server-config-context';
import { PopupMessage } from '@common/popup-message';
import { ActionBadge } from '../../../common/action-badge/action-badge';
import { AutoInstallPreset } from '../../../common/auto-install-preset';
import styles from './base-plugins-rule.css';
export function BasePluginsRule(props) {
    var _a;
    const { children, unsaved, initiallyCollapsed, className, ruleName, onRemoveClick, rule, extCollapsed, setExtCollapsed, rdEnabled } = props;
    const { action } = rule;
    const [internalCollapsed, setInternalCollapsed] = useState(initiallyCollapsed);
    const collapsed = extCollapsed !== null && extCollapsed !== void 0 ? extCollapsed : internalCollapsed;
    const setCollapsed = setExtCollapsed !== null && setExtCollapsed !== void 0 ? setExtCollapsed : setInternalCollapsed;
    const ref = useRef(null);
    const [isFocused] = useIsFocused(ref, !initiallyCollapsed);
    const prevCollapsed = usePrevious(collapsed);
    const prevUnsaved = usePrevious(unsaved);
    const toggleCollapsed = () => setCollapsed(!collapsed);
    const handleRemoveClick = useCallback(() => onRemoveClick(), [onRemoveClick]);
    const remoteDevelopmentDisabledPluginsUi = useFeatureFlag('remoteDevelopmentDisabledPluginsUi');
    const showRdWarning = remoteDevelopmentDisabledPluginsUi
        ? rdEnabled && isRdForRuleNotSupported(rule)
        : false;
    const hasBadges = unsaved || (collapsed && action);
    return (_jsxs("div", { className: classNames(styles.wrapper, className, {
            [styles.unsaved]: unsaved,
            [styles.collapsed]: collapsed,
            [styles.focused]: isFocused
        }), ref: ref, ...assignTestId('plugins-rule', { collapsed, expanded: !collapsed }, props), children: [_jsxs("div", { className: styles.ruleHeader, onClick: toggleCollapsed, children: [_jsx(Icon, { className: styles.chevron, glyph: chevronDown }), _jsx(H3, { className: styles.ruleName, title: typeof ruleName === 'string' ? ruleName : undefined, children: typeof ruleName === 'function' ? ruleName(collapsed) : ruleName }), showRdWarning && _jsx(RDNotSupportedIcon, {}), hasBadges && (_jsxs("div", { className: styles.badges, children: [unsaved && (_jsx(Badge, { variant: Badge.variant.Filled, color: Badge.color.Primary, size: Badge.size.Lg, className: classNames({
                                    [styles.appear]: prevUnsaved === false
                                }), children: "Unsaved changes" })), collapsed && action && (_jsx(ActionBadge, { className: classNames({
                                    [styles.appear]: prevCollapsed === false
                                }), action: action, autoInstallPreset: rule.type === 'single-plugin' && ((_a = rule.singlePluginTypeRule) === null || _a === void 0 ? void 0 : _a.type) === 'bundled'
                                    ? AutoInstallPreset.Enable
                                    : AutoInstallPreset.AutoInstall }))] })), _jsx(Button, { className: classNames(styles.remove), icon: trash, onClick: handleRemoveClick, ...assignTestId('remove-rule') })] }), _jsx(Collapsible, { collapsed: collapsed, children: _jsx("div", { className: styles.ruleBody, children: children }) })] }));
}
function RDNotSupportedIcon() {
    return (_jsx(PopupMessage, { className: styles.rdIconWrapper, anchor: _jsx(Icon, { className: styles.rdIcon, glyph: info }), children: "This rule type isn't supported in the remote development environment and will have no effect there. It will still work as expected in local IDEs and tools." }));
}
function isRdForRuleNotSupported(rule) {
    if (!rule.action) {
        return false;
    }
    switch (rule.action) {
        case 'force-auto-install':
        case 'auto-install':
            return false;
        default:
            return true;
    }
}
